import { useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import noop from 'lib/function/noop'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import ChatItemDropdown from './ChatItemDropdown'
import ModalContext from 'contexts/ModalContext'
import ConfirmDeleteStopModal from './ConfirmDeleteStopModal'
import { CANVAS_CONTENT_WIDTH } from './constants'
import Heading from 'LuxKit/Typography/Heading'
import { buildDeleteStopMessage } from '../utils/deleteTripMessages'

const OriginContainer = styled.div`
  width: ${rem(CANVAS_CONTENT_WIDTH)};
`

const IconCircle = styled.div`
  width: ${rem(32)};
  height: ${rem(32)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  background: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: ${props => props.theme.shadow.flat.small};
  font-size: ${rem(20)};
  position: relative;
  z-index: 2;
`

interface Props {
  stopId: number
  placeName: string
  placeCountry: string
  countryFlag?: string
  onSendMessage?: (message: string) => void
}

function TripPlanOriginStop({
  stopId,
  placeName,
  placeCountry,
  countryFlag,
  onSendMessage = noop,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const handleDelete = useCallback(async() => {
    const confirmed = await showModal<boolean>(<ConfirmDeleteStopModal placeName={placeName} />)
    if (!confirmed) return
    onSendMessage(buildDeleteStopMessage({ intl, stop: { id: stopId, placeName } }))
  }, [showModal, placeName, onSendMessage, intl, stopId])

  return (
    <OriginContainer>
      <Group direction="horizontal" gap={12} horizontalAlign="space-between" verticalAlign="start">
        <Group direction="horizontal" gap={20} verticalAlign="center" horizontalAlign="center">
          {countryFlag && <IconCircle>{countryFlag}</IconCircle>}
          <Group direction="vertical" gap={2}>
            <Caption variant="medium" colour="neutral-three">
              <FormattedMessage
                id="grOmPd5l2L1uDX16RpKy"
                defaultMessage="Your location"
                description="Label above the origin city name in the trip itinerary"
              />
            </Caption>
            <Heading variant="heading4" as="span">
              {intl.formatMessage(
                {
                  id: 'QLcnY5cc5IZmwxMJLL6L',
                  defaultMessage: '{placeName}, {placeCountry}',
                  description: 'Origin place name and country shown in the trip itinerary; translators may reorder the place and country and adjust the separator',
                },
                {
                  placeName,
                  placeCountry,
                },
              )}
            </Heading>
          </Group>
        </Group>
        <ChatItemDropdown onDelete={handleDelete} />
      </Group>
    </OriginContainer>
  )
}

export default TripPlanOriginStop
